/**
 * @fileoverview Profit & Loss Statement Component
 * Professional P&L statement with income and expense analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ACCOUNT_CATEGORIES, 
  CATEGORY_DEFINITIONS, 
  getAccountLinesByCategory
} from '../../models/Account';
import './ProfitLoss.css';

/**
 * ProfitLoss Component
 * @param {Object} props - Component props
 * @param {Array} props.financialData - Financial data array
 * @param {string} props.entityName - Entity name for display
 * @param {string} props.currency - Currency code
 * @param {Date} props.periodStart - Period start date
 * @param {Date} props.periodEnd - Period end date
 */
const ProfitLoss = ({ 
  financialData = [], 
  entityName = "Entity", 
  currency = "USD",
  periodStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  periodEnd = new Date()
}) => {
  // Period selection state
  const [selectedPeriod, setSelectedPeriod] = useState('mtd');
  const [effectivePeriodStart, setEffectivePeriodStart] = useState(periodStart);
  const [effectivePeriodEnd, setEffectivePeriodEnd] = useState(periodEnd);
  const [showCustomPeriodSelector, setShowCustomPeriodSelector] = useState(false);
  const [customPeriodType, setCustomPeriodType] = useState('quarter'); // 'quarter' or 'year'
  
  // Initialize with current quarter and year
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
  const currentYearShort = currentYear.toString().slice(-2);
  
  const [customQuarter, setCustomQuarter] = useState(`${currentQuarter}Q${currentYearShort}`);
  const [customYear, setCustomYear] = useState(currentYear.toString());

  // Calculate period dates
  const calculatePeriodDates = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);

    return {
      mtd: {
        start: new Date(currentYear, currentMonth, 1),
        end: now
      },
      qtd: {
        start: new Date(currentYear, currentQuarter * 3, 1),
        end: now
      },
      ytd: {
        start: new Date(currentYear, 0, 1),
        end: now
      },
      lastYear: {
        start: new Date(currentYear - 1, 0, 1),
        end: new Date(currentYear - 1, 11, 31)
      }
    };
  };

  const periodDates = calculatePeriodDates();

  // Parse custom quarter (e.g., "1Q24" -> Q1 2024)
  const parseCustomQuarter = (quarterStr) => {
    const match = quarterStr.match(/^(\d)Q(\d{2})$/);
    if (!match) return null;
    
    const quarter = parseInt(match[1]);
    const year = 2000 + parseInt(match[2]);
    
    const startMonth = (quarter - 1) * 3;
    const endMonth = startMonth + 2;
    
    return {
      start: new Date(year, startMonth, 1),
      end: new Date(year, endMonth + 1, 0) // Last day of quarter
    };
  };

  // Parse custom year (e.g., "2024" -> Full year 2024)
  const parseCustomYear = (yearStr) => {
    const year = parseInt(yearStr);
    return {
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31)
    };
  };

  // Handle period selection
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setShowCustomPeriodSelector(false);
    
    if (period === 'custom') {
      setShowCustomPeriodSelector(true);
      handleCustomPeriodUpdate();
    } else {
      const dates = periodDates[period];
      if (dates) {
        setEffectivePeriodStart(dates.start);
        setEffectivePeriodEnd(dates.end);
      }
    }
  };

  // Handle custom period updates
  const handleCustomPeriodUpdate = useCallback(() => {
    let dates = null;
    
    if (customPeriodType === 'quarter') {
      dates = parseCustomQuarter(customQuarter);
    } else if (customPeriodType === 'year') {
      dates = parseCustomYear(customYear);
    }
    
    if (dates) {
      setEffectivePeriodStart(dates.start);
      setEffectivePeriodEnd(dates.end);
    }
  }, [customPeriodType, customQuarter, customYear]);

  // Update effective dates when custom values change
  useEffect(() => {
    if (selectedPeriod === 'custom') {
      handleCustomPeriodUpdate();
    }
  }, [selectedPeriod, handleCustomPeriodUpdate]);

  // Update effective dates when props change
  useEffect(() => {
    if (selectedPeriod === 'mtd' || selectedPeriod === 'qtd' || selectedPeriod === 'ytd') {
      const dates = calculatePeriodDates()[selectedPeriod];
      if (dates) {
        setEffectivePeriodStart(dates.start);
        setEffectivePeriodEnd(dates.end);
      }
    }
  }, [selectedPeriod, periodStart, periodEnd]);

  // Generate quarter options for custom selector
  const generateQuarterOptions = () => {
    const options = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= currentYear - 3; year--) {
      for (let quarter = 4; quarter >= 1; quarter--) {
        const yearShort = year.toString().slice(-2);
        options.push(`${quarter}Q${yearShort}`);
      }
    }
    
    return options;
  };

  // Generate year options for custom selector
  const generateYearOptions = () => {
    const options = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= currentYear - 5; year--) {
      options.push(year.toString());
    }
    
    return options;
  };

  // Group financial data by account line ID
  const dataByAccountLine = {};
  financialData.forEach(data => {
    dataByAccountLine[data.accountLineId] = data;
  });

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount));
  };

  // Format date range
  const formatDateRange = (start, end) => {
    const startStr = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(start));
    
    const endStr = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    }).format(new Date(end));
    
    return `${startStr} - ${endStr}`;
  };

  // Calculate category totals
  const calculateCategoryTotal = (categoryType) => {
    const accountLines = getAccountLinesByCategory(categoryType);
    return accountLines.reduce((total, line) => {
      const data = dataByAccountLine[line.id];
      if (data && data.balance !== undefined) {
        return total + Math.abs(data.balance);
      }
      return total;
    }, 0);
  };

  // Render account lines for a category
  const renderAccountLines = (categoryType) => {
    const accountLines = getAccountLinesByCategory(categoryType);
    const categoryDef = CATEGORY_DEFINITIONS[categoryType];
    
    if (accountLines.length === 0) return null;

    return (
      <div className="pl-category">
        <div className="category-header">
          <span className="category-icon">{categoryDef.icon}</span>
          <h3 className="category-title">{categoryDef.name}</h3>
        </div>
        
        <div className="account-lines">
          {accountLines.map(line => {
            const data = dataByAccountLine[line.id];
            const balance = data?.balance || 0;
            const previousBalance = data?.previousBalance || 0;
            const change = balance - previousBalance;
            const changePercent = previousBalance !== 0 ? (change / Math.abs(previousBalance)) * 100 : 0;
            
            if (Math.abs(balance) < 1) return null; // Skip zero balances
            
            return (
              <div key={line.id} className="account-line">
                <div className="account-info">
                  <span className="account-name">{line.name}</span>
                  <span className="account-code">{line.code}</span>
                </div>
                <div className="account-amounts">
                  <div className="current-amount">
                    {formatCurrency(balance)}
                  </div>
                  {change !== 0 && (
                    <div className={`change-indicator ${change > 0 ? 'positive' : 'negative'}`}>
                      {change > 0 ? '+' : ''}{formatCurrency(change)}
                      {Math.abs(changePercent) > 0.1 && (
                        <span className="change-percent">
                          ({change > 0 ? '+' : ''}{changePercent.toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="category-total">
          <span className="total-label">Total {categoryDef.name}</span>
          <span className="total-amount">{formatCurrency(calculateCategoryTotal(categoryType))}</span>
        </div>
      </div>
    );
  };

  // Calculate key financial metrics
  const totalIncome = calculateCategoryTotal(ACCOUNT_CATEGORIES.INCOME);
  const totalExpenses = calculateCategoryTotal(ACCOUNT_CATEGORIES.EXPENSES);
  const netIncome = totalIncome - totalExpenses;
  
  // Calculate operating metrics
  const operatingRevenue = getAccountLinesByCategory(ACCOUNT_CATEGORIES.INCOME)
    .filter(line => line.metadata?.isOperating)
    .reduce((total, line) => {
      const data = dataByAccountLine[line.id];
      return total + (data?.balance || 0);
    }, 0);

  const operatingExpenses = getAccountLinesByCategory(ACCOUNT_CATEGORIES.EXPENSES)
    .filter(line => line.metadata?.isOperating)
    .reduce((total, line) => {
      const data = dataByAccountLine[line.id];
      return total + (data?.balance || 0);
    }, 0);

  const operatingIncome = operatingRevenue - operatingExpenses;

  // Calculate margins
  const grossMargin = totalIncome > 0 ? ((totalIncome - (dataByAccountLine['cost-of-sales']?.balance || 0)) / totalIncome) * 100 : 0;
  const operatingMargin = totalIncome > 0 ? (operatingIncome / totalIncome) * 100 : 0;
  const netMargin = totalIncome > 0 ? (netIncome / totalIncome) * 100 : 0;

  // Calculate cash flow metrics
  const operatingCashFlow = netIncome; // Simplified - in reality would include adjustments
  const capitalExpenditures = 0; // Would come from investing activities
  const freeCashFlow = operatingCashFlow - capitalExpenditures;
  
  // Cash flow ratios
  const cashFlowToRevenueRatio = totalIncome > 0 ? (operatingCashFlow / totalIncome) * 100 : 0;
  const cashFlowToNetIncomeRatio = netIncome !== 0 ? (operatingCashFlow / netIncome) * 100 : 0;
  const operatingCashFlowMargin = totalIncome > 0 ? (operatingCashFlow / totalIncome) * 100 : 0;

  return (
    <div className="profit-loss">
      <div className="pl-header">
        <div className="header-left">
          <h2 className="statement-title">Profit & Loss Statement</h2>
          <div className="statement-info">
            <h3 className="entity-name">{entityName}</h3>
            <p className="period-range">For the period {formatDateRange(effectivePeriodStart, effectivePeriodEnd)}</p>
          </div>
        </div>
        
        <div className="header-right">
          <div className="period-selector">
            <div className="period-buttons">
              <button 
                className={`period-btn ${selectedPeriod === 'mtd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('mtd')}
              >
                MTD
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'qtd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('qtd')}
              >
                QTD
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'ytd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('ytd')}
              >
                YTD
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'lastYear' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('lastYear')}
              >
                Last Year
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'custom' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('custom')}
              >
                Custom
              </button>
            </div>
            
            {showCustomPeriodSelector && (
              <div className="custom-period-selector">
                <div className="period-type-selector">
                  <button 
                    className={`type-btn ${customPeriodType === 'quarter' ? 'active' : ''}`}
                    onClick={() => setCustomPeriodType('quarter')}
                  >
                    Quarter
                  </button>
                  <button 
                    className={`type-btn ${customPeriodType === 'year' ? 'active' : ''}`}
                    onClick={() => setCustomPeriodType('year')}
                  >
                    Year
                  </button>
                </div>
                
                {customPeriodType === 'quarter' && (
                  <select 
                    value={customQuarter} 
                    onChange={(e) => setCustomQuarter(e.target.value)}
                    className="custom-select"
                  >
                    {generateQuarterOptions().map(quarter => (
                      <option key={quarter} value={quarter}>{quarter}</option>
                    ))}
                  </select>
                )}
                
                {customPeriodType === 'year' && (
                  <select 
                    value={customYear} 
                    onChange={(e) => setCustomYear(e.target.value)}
                    className="custom-select"
                  >
                    {generateYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pl-content">
        <div className="pl-content-grid">
          {/* Column 1: Income, Expenses & Net Income */}
          <div className="income-expenses-column">
            <div className="income-section">
              {renderAccountLines(ACCOUNT_CATEGORIES.INCOME)}
            </div>
            
            <div className="expenses-section">
              {renderAccountLines(ACCOUNT_CATEGORIES.EXPENSES)}
            </div>

            {/* Net Income - Final Result */}
            <div className="net-income-total-section">
              <div className="category-header net-income-header">
                <div className="net-income-title-row">
                  <span className="category-icon">💰</span>
                  <h3 className="category-title">Net Income</h3>
                </div>
                <div className="net-income-header-content">
                  <span className={`net-income-amount ${netIncome >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(netIncome)}
                  </span>
                  <div className="net-income-subtitle">
                    Net Margin: {netMargin.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Cash Flow Statement */}
          <div className="cashflow-column">
            <div className="cashflow-section">
              {/* Operating Cash Flow */}
              <div className="cashflow-category operating">
                <div className="category-header cashflow-operating-header">
                  <span className="category-icon">🔄</span>
                  <h3 className="category-title">Cash Flow from Operations</h3>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Net Income</span>
                  <span className="cashflow-amount">{formatCurrency(netIncome)}</span>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Depreciation & Amortization</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Working Capital Changes</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="category-total">
                  <span className="total-label">Operating Cash Flow</span>
                  <span className="total-amount">{formatCurrency(netIncome)}</span>
                </div>
              </div>

              {/* Investing Cash Flow */}
              <div className="cashflow-category investing">
                <div className="category-header cashflow-investing-header">
                  <span className="category-icon">📈</span>
                  <h3 className="category-title">Cash Flow from Investing</h3>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Capital Expenditures</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Asset Purchases/Sales</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="category-total">
                  <span className="total-label">Investing Cash Flow</span>
                  <span className="total-amount">{formatCurrency(0)}</span>
                </div>
              </div>

              {/* Financing Cash Flow */}
              <div className="cashflow-category financing">
                <div className="category-header cashflow-financing-header">
                  <span className="category-icon">🏦</span>
                  <h3 className="category-title">Cash Flow from Financing</h3>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Debt Payments</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="cashflow-line">
                  <span className="cashflow-label">Equity Changes</span>
                  <span className="cashflow-amount">{formatCurrency(0)}</span>
                </div>
                <div className="category-total">
                  <span className="total-label">Financing Cash Flow</span>
                  <span className="total-amount">{formatCurrency(0)}</span>
                </div>
              </div>

              {/* Net Cash Flow - Like TOTAL EXPENSES */}
              <div className="net-cashflow-total">
                <span className="total-label">Net Cash Flow</span>
                <span className={`total-amount ${netIncome >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(netIncome)}
                </span>
              </div>
            </div>
          </div>

          {/* Column 3: Detailed Performance Metrics */}
          <div className="metrics-column">
            <div className="detailed-metrics">
              <h4 className="detailed-metrics-title">Performance Metrics</h4>
              
              {/* Financial Overview */}
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Total Revenue</span>
                <span className="detailed-metric-value">{formatCurrency(totalIncome)}</span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Total Expenses</span>
                <span className="detailed-metric-value">{formatCurrency(totalExpenses)}</span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Net Result</span>
                <span className={`detailed-metric-value ${netIncome >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(netIncome)}
                </span>
              </div>
              
              {/* Profitability Ratios */}
              <div className="metrics-divider">Profitability</div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Gross Margin</span>
                <span className="detailed-metric-value">{grossMargin.toFixed(1)}%</span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Operating Margin</span>
                <span className="detailed-metric-value">{operatingMargin.toFixed(1)}%</span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Net Margin</span>
                <span className={`detailed-metric-value ${netMargin >= 0 ? 'positive' : 'negative'}`}>
                  {netMargin.toFixed(1)}%
                </span>
              </div>
              
              {/* Cash Flow Ratios */}
              <div className="metrics-divider">Cash Flow</div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Operating Cash Flow</span>
                <span className={`detailed-metric-value ${operatingCashFlow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(operatingCashFlow)}
                </span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Cash Flow Margin</span>
                <span className={`detailed-metric-value ${operatingCashFlowMargin >= 0 ? 'positive' : 'negative'}`}>
                  {operatingCashFlowMargin.toFixed(1)}%
                </span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Cash to Revenue</span>
                <span className={`detailed-metric-value ${cashFlowToRevenueRatio >= 0 ? 'positive' : 'negative'}`}>
                  {cashFlowToRevenueRatio.toFixed(1)}%
                </span>
              </div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Cash to Net Income</span>
                <span className={`detailed-metric-value ${cashFlowToNetIncomeRatio >= 0 ? 'positive' : 'negative'}`}>
                  {cashFlowToNetIncomeRatio.toFixed(1)}%
                </span>
              </div>

              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Free Cash Flow</span>
                <span className={`detailed-metric-value ${freeCashFlow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(freeCashFlow)}
                </span>
              </div>

              {/* Overall Performance */}
              <div className="metrics-divider">Performance</div>
              
              <div className="detailed-metric-item">
                <span className="detailed-metric-label">Status</span>
                <span className={`detailed-metric-value ${netIncome >= 0 ? 'positive' : 'negative'}`}>
                  {netIncome >= 0 ? 'Profitable' : 'Loss'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {financialData.length === 0 && (
        <div className="no-data-message">
          <p>No financial data available for this period.</p>
          <p>Connect data sources or manually enter income and expense data to view the P&L statement.</p>
        </div>
      )}
    </div>
  );
};

export default ProfitLoss; 